import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChunkedText } from './textChunkerSlice';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Paper, 
  Fade, 
  Zoom, 
  CircularProgress,
  useTheme,
  Container,
  Stack,
  Chip,
  Divider,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { 
  Send, 
  ContentCut, 
  AutoAwesome, 
  Refresh,
  ContentCopy,
  Download,
  Twitter,
  LinkedIn,
  Instagram,
  Facebook,
  Settings,
  CheckCircle,
  Warning,
  Info,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../theme/ThemeProvider';
import QuickExamples from '../../components/shared/QuickExamples';

// Platform configurations
const PLATFORMS = {
  twitter: {
    name: 'Twitter/X',
    icon: <Twitter />,
    maxChars: 280,
    color: '#1DA1F2',
    description: 'Perfect for Twitter threads and X posts',
  },
  linkedin: {
    name: 'LinkedIn',
    icon: <LinkedIn />,
    maxChars: 1300,
    color: '#0077B5',
    description: 'Professional posts for LinkedIn',
  },
  instagram: {
    name: 'Instagram',
    icon: <Instagram />,
    maxChars: 2200,
    color: '#E4405F',
    description: 'Captions for Instagram posts',
  },
  facebook: {
    name: 'Facebook',
    icon: <Facebook />,
    maxChars: 63206,
    color: '#1877F2',
    description: 'Long-form posts for Facebook',
  },
  custom: {
    name: 'Custom',
    icon: <Settings />,
    maxChars: 500,
    color: '#8b5cf6',
    description: 'Custom character limit',
  },
};

const PostSplitter = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const messagesEndRef = useRef(null);
  
  const chunks = useSelector(state => state.textChunker.chunks);
  const isLoading = useSelector(state => state.textChunker.loading);
  const error = useSelector(state => state.textChunker.error);

  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('twitter');
  const [customCharLimit, setCustomCharLimit] = useState(500);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTextSplitting = (results) => {
    if (!results || !results.chunks) return 'No text splitting results available.';
    
    const { chunks } = results;
    
    if (chunks.length === 0) {
      return 'Unable to split the text into chunks.';
    }
    
    const platform = PLATFORMS[selectedPlatform];
    
    let analysis = `✂️ **Text Split Successfully!**\n\n`;
    analysis += `📱 **Platform: ${platform.name}**\n`;
    analysis += `📏 **Character Limit: ${platform.maxChars}**\n`;
    analysis += `📦 **Total Chunks: ${chunks.length}**\n\n`;
    
    // Character usage analysis
    const totalChars = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const avgCharsPerChunk = Math.round(totalChars / chunks.length);
    const efficiency = Math.round((avgCharsPerChunk / platform.maxChars) * 100);
    
    analysis += `📊 **Analysis:**\n`;
    analysis += `• Total characters: ${totalChars}\n`;
    analysis += `• Average per chunk: ${avgCharsPerChunk} characters\n`;
    analysis += `• Efficiency: ${efficiency}% of limit used\n\n`;
    
    // Efficiency rating
    if (efficiency > 90) {
      analysis += `✅ **Excellent efficiency** - Making great use of the character limit!\n\n`;
    } else if (efficiency > 70) {
      analysis += `👍 **Good efficiency** - Good balance between content and readability.\n\n`;
    } else if (efficiency > 50) {
      analysis += `⚠️ **Moderate efficiency** - Consider adding more content or adjusting splits.\n\n`;
    } else {
      analysis += `🔴 **Low efficiency** - Lots of unused space. Consider combining chunks.\n\n`;
    }
    
    // Recommendations
    analysis += `💡 **Recommendations:**\n`;
    if (chunks.length === 1) {
      analysis += `• Perfect! Your text fits in a single ${platform.name} post.\n`;
    } else {
      analysis += `• Your text will be posted as a ${platform.name} thread.\n`;
      analysis += `• Each chunk is optimized for ${platform.name}'s character limit.\n`;
      analysis += `• Consider adding hashtags or mentions to each chunk.\n`;
    }
    
    if (platform.name === 'Twitter/X') {
      analysis += `• Use "1/3", "2/3", "3/3" format for clear thread structure.\n`;
    } else if (platform.name === 'LinkedIn') {
      analysis += `• Add professional hashtags and engage with comments.\n`;
    } else if (platform.name === 'Instagram') {
      analysis += `• Include relevant hashtags and emojis for better reach.\n`;
    }
    
    return analysis;
  };

  const handleSubmit = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setShowWelcome(false);

    try {
      const platform = PLATFORMS[selectedPlatform];
      const chunkSize = selectedPlatform === 'custom' ? customCharLimit : platform.maxChars;
      
      const result = await dispatch(fetchChunkedText({ 
        text: inputText, 
        filters: {
          chunkSize,
          preserveLineBreaks: true,
          numberingFormat: 'x/y',
          suffix: '>>',
        }
      })).unwrap();

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: formatTextSplitting(result),
        originalText: inputText,
        results: result,
        platform: selectedPlatform,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        text: 'Sorry, I couldn\'t split your text. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const copyAllChunks = (chunks) => {
    if (chunks && chunks.length > 0) {
      const allText = chunks.join('\n\n');
      navigator.clipboard.writeText(allText);
    }
  };

  const downloadConversation = () => {
    const content = messages
      .filter(msg => msg.type === 'bot' && msg.results?.chunks)
      .map(msg => `${msg.originalText}\n\n${msg.text}\n\nChunks:\n${msg.results.chunks.join('\n\n---\n\n')}\n\n`)
      .join('---\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-splitting-analysis.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearConversation = () => {
    setMessages([]);
    setShowWelcome(true);
  };

  const quickExamples = [
    "This is an example of a long text that exceeds Twitter's 280-character limit. The tool will split this text into smaller tweets, preserving readability and structure while maintaining the natural flow of your content.",
    "Creating engaging social media content requires careful planning. This tool helps you break down complex ideas into digestible pieces that your audience can easily consume and engage with.",
    "Whether you're writing a Twitter thread, LinkedIn article, or Instagram caption, this tool ensures your message reaches its full potential by optimizing for each platform's unique characteristics.",
  ];

  const handleQuickExample = (example) => {
    setInputText(example);
  };

  const handlePlatformChange = (platform) => {
    setSelectedPlatform(platform);
  };

  const renderTextSplittingMessage = (message) => {
    if (!message.results || !message.results.chunks) return message.text;
    
    const { chunks } = message.results;
    const platform = PLATFORMS[message.platform || selectedPlatform];
    
    return (
      <Box>
        {/* Splitting Summary */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2,
          p: 2,
          background: `${platform.color}15`,
          borderRadius: 2,
          border: `1px solid ${platform.color}30`,
        }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: '50%', 
            background: platform.color,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ContentCut />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: platform.color }}>
              {chunks.length} Chunks Created
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Optimized for {platform.name} ({platform.maxChars} chars)
            </Typography>
          </Box>
        </Box>
        
        {/* Detailed Analysis */}
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, mb: 3 }}>
          {message.text}
        </Typography>
        
        {/* Chunks Display */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Generated Chunks
          </Typography>
          <Stack spacing={2}>
            {chunks.map((chunk, index) => (
              <Fade in timeout={200 + index * 100} key={index}>
                <Card
                  sx={{
                    background: darkMode ? 'rgba(51, 65, 85, 0.3)' : 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${platform.color}30`,
                    '&:hover': {
                      borderColor: platform.color,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    {/* Chunk Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label={`Chunk ${index + 1} of ${chunks.length}`}
                        size="small"
                        sx={{
                          background: platform.color,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {chunk.length} / {platform.maxChars} chars
                      </Typography>
                    </Box>
                    
                    {/* Character Usage Bar */}
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(chunk.length / platform.maxChars) * 100}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            background: platform.color,
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>
                    
                    {/* Chunk Content */}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        background: darkMode ? 'rgba(51, 65, 85, 0.2)' : 'rgba(248, 250, 252, 0.5)',
                        p: 1.5,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {chunk}
                    </Typography>
                    
                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Copy chunk">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(chunk)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: platform.color }
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {message.platform === 'twitter' && (
                        <Tooltip title="Tweet this chunk">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const tweetText = encodeURIComponent(chunk);
                              const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;
                              window.open(tweetUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: platform.color }
                            }}
                          >
                            <Twitter fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </Stack>
          
          {/* Copy All Button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Chip
              icon={<ContentCopy />}
              label="Copy All Chunks"
              onClick={() => copyAllChunks(chunks)}
              sx={{
                background: platform.color,
                color: 'white',
                cursor: 'pointer',
                '&:hover': {
                  background: platform.color,
                  opacity: 0.8,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ height: '100vh', py: 2 }}>
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: darkMode ? 'rgba(51, 65, 85, 0.1)' : 'rgba(248, 250, 252, 0.5)',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}>
        
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%)`,
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <ContentCut sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Text Splitter Magic ✂️
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Split your text into perfect chunks for any platform
            </Typography>
          </Box>
        </Box>

        {/* Platform Selection */}
        <Box sx={{ 
          p: 2, 
          background: darkMode ? 'rgba(51, 65, 85, 0.2)' : 'rgba(255, 255, 255, 0.8)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
            Target Platform
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(PLATFORMS).map(([key, platform]) => (
              <Chip
                key={key}
                icon={platform.icon}
                label={`${platform.name} (${platform.maxChars})`}
                onClick={() => handlePlatformChange(key)}
                sx={{
                  background: selectedPlatform === key ? platform.color : 'transparent',
                  color: selectedPlatform === key ? 'white' : 'text.primary',
                  border: `1px solid ${selectedPlatform === key ? platform.color : theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': {
                    background: selectedPlatform === key ? platform.color : 'rgba(0,0,0,0.04)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Messages Area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          background: darkMode ? 'rgba(51, 65, 85, 0.05)' : 'rgba(255, 255, 255, 0.3)',
        }}>
          {showWelcome && (
            <Fade in timeout={800}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AutoAwesome sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  Welcome to Text Splitter Magic! ✂️
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Split your long text into perfect chunks for any social media platform
                </Typography>
                
                <QuickExamples 
                  examples={quickExamples} 
                  onExampleClick={handleQuickExample} 
                />
              </Box>
            </Fade>
          )}

          {messages.map((message, index) => (
            <Fade in timeout={300 + index * 100} key={message.id}>
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start' 
              }}>
                <Paper
                  elevation={2}
                  sx={{
                    maxWidth: '90%',
                    p: 2,
                    background: message.type === 'user' 
                      ? `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`
                      : darkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                    color: message.type === 'user' ? 'white' : 'text.primary',
                    borderRadius: 3,
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {message.type === 'bot' && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Splitting: {message.originalText.substring(0, 50)}...
                      </Typography>
                    </Box>
                  )}
                  
                  {message.type === 'bot' ? (
                    renderTextSplittingMessage(message)
                  ) : (
                    <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                      {message.text}
                    </Typography>
                  )}
                </Paper>
              </Box>
            </Fade>
          ))}
          
          {isLoading && (
            <Fade in timeout={300}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    background: darkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minWidth: 200,
                  }}
                >
                  <CircularProgress size={20} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Splitting text...
                    </Typography>
                    <LinearProgress sx={{ mt: 1, width: 100 }} />
                  </Box>
                </Paper>
              </Box>
            </Fade>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ 
          p: 2, 
          background: darkMode ? 'rgba(51, 65, 85, 0.3)' : 'rgba(255, 255, 255, 0.8)',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your text to split for social media... ✂️"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  background: darkMode ? 'rgba(51, 65, 85, 0.2)' : 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    background: darkMode ? 'rgba(51, 65, 85, 0.3)' : 'rgba(255, 255, 255, 1)',
                  },
                  '&.Mui-focused': {
                    background: darkMode ? 'rgba(51, 65, 85, 0.4)' : 'rgba(255, 255, 255, 1)',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSubmit}
              disabled={!inputText.trim() || isLoading}
              sx={{
                background: `linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)`,
                color: 'white',
                p: 1.5,
                '&:hover': {
                  background: `linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)`,
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Send />
            </IconButton>
          </Box>
          
          {/* Action Buttons */}
          {messages.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
              <IconButton
                onClick={downloadConversation}
                sx={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  color: 'primary.main',
                  '&:hover': { background: 'rgba(102, 126, 234, 0.2)' }
                }}
              >
                <Download />
              </IconButton>
              <IconButton
                onClick={clearConversation}
                sx={{
                  background: 'rgba(255, 107, 107, 0.1)',
                  color: 'error.main',
                  '&:hover': { background: 'rgba(255, 107, 107, 0.2)' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default PostSplitter;
